import React, { useState, useEffect, useRef } from "react";
import { X, User, Plus, Trash2, Mail } from "lucide-react";
import { useCalendarSuggestions } from "../../hooks/useCalendarSuggestions";

const ParticipantsModal = ({
	participants,
	onAdd,
	onChange,
	onClose,
	onSendUpdatesChange,
}) => {
	const [email, setEmail] = useState("");
	const [error, setError] = useState("");
	const [showSuggestions, setShowSuggestions] = useState(false);
	const suggestionsRef = useRef(null);
	const inputRef = useRef(null);
	const {
		suggestions,
		recentSelections,
		loading,
		fetchSuggestions,
		addToCache,
	} = useCalendarSuggestions();

	const [showSendUpdates, setShowSendUpdates] = useState(false);

	const handleDone = () => {
		if (participants.length > 0) {
			setShowSendUpdates(true);
		} else {
			onClose();
		}
	};

	const SendUpdatesPopup = () => {
		const [settings, setSettings] = useState({
			sendUpdates: "all",
			guestPermissions: {
				canModify: false,
				canInviteOthers: true,
				canSeeGuestList: true,
			},
		});

		// Modified to only update settings
		const handleSendUpdatesSelect = (sendUpdatesValue) => {
			setSettings((prev) => ({
				...prev,
				sendUpdates: sendUpdatesValue,
			}));
		};

		// New handler for done button
		const handleDone = () => {
			onSendUpdatesChange({
				sendUpdates: settings.sendUpdates,
				guestPermissions: settings.guestPermissions,
			});
			onClose();
		};

		return (
			<div className="absolute inset-0 flex items-center justify-center bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm">
				<div className="w-[90%] max-w-sm bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6">
					<div className="space-y-6">
						<div className="flex items-center justify-between">
							<h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
								Send invites?
							</h3>
							<button
								type="button"
								onClick={() => setShowSendUpdates(false)}
								className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
							>
								<X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
							</button>
						</div>

						{/* Email Options */}
						<div className="space-y-2">
							<button
								type="button"
								onClick={() => handleSendUpdatesSelect("all")}
								className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-100/80 dark:hover:bg-gray-700/80 rounded-lg text-gray-700 dark:text-gray-200 transition-colors ${
									settings.sendUpdates === "all"
										? "bg-gray-100 dark:bg-gray-700"
										: ""
								}`}
							>
								<Mail className="w-5 h-5 text-gray-500 dark:text-gray-400" />
								<div className="text-left">
									<div className="text-sm font-medium">
										Send to all participants
									</div>
									<div className="text-xs text-gray-500 dark:text-gray-400">
										Everyone will receive an email invitation
									</div>
								</div>
							</button>

							<button
								type="button"
								onClick={() => handleSendUpdatesSelect("externalOnly")}
								className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-100/80 dark:hover:bg-gray-700/80 rounded-lg text-gray-700 dark:text-gray-200 transition-colors ${
									settings.sendUpdates === "externalOnly"
										? "bg-gray-100 dark:bg-gray-700"
										: ""
								}`}
							>
								<Mail className="w-5 h-5 text-gray-500 dark:text-gray-400" />
								<div className="text-left">
									<div className="text-sm font-medium">
										Non-Google users only
									</div>
									<div className="text-xs text-gray-500 dark:text-gray-400">
										Only external participants receive emails
									</div>
								</div>
							</button>
						</div>

						{/* Guest Permissions */}
						<div className="space-y-3 pt-2 border-t border-gray-200 dark:border-gray-700">
							<div className="text-sm font-medium text-gray-900 dark:text-gray-100">
								Guest permissions
							</div>
							<div className="space-y-3">
								<label className="flex items-center gap-2 text-gray-700 dark:text-gray-200">
									<input
										type="checkbox"
										checked={settings.guestPermissions.canModify}
										onChange={(e) =>
											setSettings((prev) => ({
												...prev,
												guestPermissions: {
													...prev.guestPermissions,
													canModify: e.target.checked,
												},
											}))
										}
										className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 dark:text-blue-400 focus:ring-blue-500 dark:focus:ring-blue-400 dark:bg-gray-700"
									/>
									<span className="text-sm">Modify event</span>
								</label>

								<label className="flex items-center gap-2 text-gray-700 dark:text-gray-200">
									<input
										type="checkbox"
										checked={settings.guestPermissions.canInviteOthers}
										onChange={(e) =>
											setSettings((prev) => ({
												...prev,
												guestPermissions: {
													...prev.guestPermissions,
													canInviteOthers: e.target.checked,
												},
											}))
										}
										className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 dark:text-blue-400 focus:ring-blue-500 dark:focus:ring-blue-400 dark:bg-gray-700"
									/>
									<span className="text-sm">Invite others</span>
								</label>

								<label className="flex items-center gap-2 text-gray-700 dark:text-gray-200">
									<input
										type="checkbox"
										checked={settings.guestPermissions.canSeeGuestList}
										onChange={(e) =>
											setSettings((prev) => ({
												...prev,
												guestPermissions: {
													...prev.guestPermissions,
													canSeeGuestList: e.target.checked,
												},
											}))
										}
										className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 dark:text-blue-400 focus:ring-blue-500 dark:focus:ring-blue-400 dark:bg-gray-700"
									/>
									<span className="text-sm">See guest list</span>
								</label>
							</div>
						</div>

						{/* Done Button */}
						<div className="pt-4 border-t border-gray-200 dark:border-gray-700">
							<button
								type="button"
								onClick={() => {
									handleDone();
								}}
								className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
							>
								Done
							</button>
						</div>
					</div>
				</div>
			</div>
		);
	};

	// Fetch suggestions when typing
	useEffect(() => {
		const debounceTimeout = setTimeout(() => {
			if (email.trim()) {
				fetchSuggestions(email.trim());
				setShowSuggestions(true);
			} else {
				setShowSuggestions(false);
			}
		}, 300);

		return () => clearTimeout(debounceTimeout);
	}, [email, fetchSuggestions]);

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (
				suggestionsRef.current &&
				!suggestionsRef.current.contains(event.target) &&
				inputRef.current &&
				!inputRef.current.contains(event.target)
			) {
				setShowSuggestions(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	const handleAddParticipant = (emailToAdd) => {
		const trimmedEmail = emailToAdd.trim().toLowerCase();

		if (!trimmedEmail) return;

		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		if (!emailRegex.test(trimmedEmail)) {
			setError("Please enter a valid email address");
			return;
		}

		if (participants.includes(trimmedEmail)) {
			setError("This participant has already been added");
			return;
		}

		onAdd(trimmedEmail);
		setEmail("");
		setError("");
		setShowSuggestions(false);
	};

	const handleRemoveParticipant = (emailToRemove) => {
		const newParticipants = participants.filter(
			(email) => email !== emailToRemove
		);
		onChange(newParticipants);
	};

	const handleSuggestionClick = (suggestion) => {
		handleAddParticipant(suggestion.email);
		addToCache(suggestion);
	};

	const handleKeyDown = (e) => {
		if (error) setError("");

		if (e.key === "Enter") {
			e.preventDefault();
			handleAddParticipant(email);
		} else if (e.key === "Escape") {
			setShowSuggestions(false);
		}
	};

	const renderSuggestions = () => {
		if (!showSuggestions || !suggestions.length) return null;

		return (
			<div
				ref={suggestionsRef}
				className="absolute z-20 w-full mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 max-h-48 overflow-auto"
			>
				{suggestions.map((suggestion) => (
					<button
						key={suggestion.email}
						type="button"
						onClick={() => handleSuggestionClick(suggestion)}
						className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
					>
						<User className="w-4 h-4 text-gray-500 dark:text-gray-400" />
						<div className="flex flex-col">
							<span className="text-sm font-medium text-gray-900 dark:text-gray-100">
								{suggestion.displayName}
							</span>
							<span className="text-xs text-gray-500 dark:text-gray-400">
								{suggestion.email}
							</span>
						</div>
					</button>
				))}
			</div>
		);
	};

	const filteredRecentSelections = recentSelections
		?.filter((recent) => !participants.includes(recent.email.toLowerCase()))
		.slice(0, 3);

	return (
		<>
			<div
				className="fixed inset-0 bg-black/20 dark:bg-black/40 z-[1000] backdrop-blur-sm"
				onClick={(e) => {
					if (!showSendUpdates) {
						onClose();
					}
				}}
			/>

			<div className="fixed z-[1001] top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-white dark:bg-gray-900 rounded-xl shadow-xl flex flex-col h-[55vh]">
				{/* Sticky Header */}
				<div className="sticky top-0 z-20 bg-white dark:bg-gray-900 rounded-t-xl">
					<div className="flex items-center justify-between px-4 py-4 border-b border-gray-100 dark:border-gray-800">
						<h2 className="text-xl font-semibold text-gray-900 dark:text-white">
							Add participants
						</h2>
						<button
							type="button"
							onClick={onClose}
							className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
						>
							<X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
						</button>
					</div>

					{/* Search Input */}
					<div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800">
						<div className="relative">
							<input
								ref={inputRef}
								type="email"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								onKeyDown={handleKeyDown}
								onFocus={() => {
									if (email.trim()) setShowSuggestions(true);
								}}
								placeholder="Enter email address"
								className={`w-full px-3 py-2 bg-gray-100/50 dark:bg-gray-800/50 rounded-lg 
					  text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400
					  focus:outline-none focus:ring-2 
					  ${error ? "ring-2 ring-red-500 focus:ring-red-500" : "focus:ring-blue-500"}`}
							/>
							<button
								type="button"
								onClick={() => handleAddParticipant(email)}
								disabled={!email.trim()}
								className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full
					  ${
							email.trim()
								? "text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/50"
								: "text-gray-400 dark:text-gray-600 cursor-not-allowed"
						}`}
							>
								<Plus className="w-5 h-5" />
							</button>
							{renderSuggestions()}
						</div>
						{error && (
							<p className="text-sm text-red-500 dark:text-red-400 mt-2">
								{error}
							</p>
						)}
					</div>
				</div>

				{/* Scrollable Content */}
				<div className="flex-1 overflow-y-auto min-h-0 px-4 py-4 scrollbar scrollbar-track-transparent scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 hover:scrollbar-thumb-gray-300 dark:hover:scrollbar-thumb-gray-600">
					{participants.length > 0 && (
						<div className="space-y-2">
							<div className="text-sm font-medium text-gray-700 dark:text-gray-300">
								{participants.length} Participant
								{participants.length !== 1 ? "s" : ""}
							</div>
							<div className="space-y-1">
								{participants.map((participantEmail) => (
									<div
										key={participantEmail}
										className="flex items-center justify-between px-3 py-2 bg-gray-100/50 dark:bg-gray-800/50 rounded-lg group"
									>
										<div className="flex items-center gap-2">
											<User className="w-4 h-4 text-gray-500 dark:text-gray-400" />
											<span className="text-sm text-gray-900 dark:text-gray-100">
												{participantEmail}
											</span>
										</div>
										<button
											type="button"
											onClick={() => handleRemoveParticipant(participantEmail)}
											className="p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity
							hover:bg-gray-200 dark:hover:bg-gray-700
							text-gray-500 dark:text-gray-400
							hover:text-red-600 dark:hover:text-red-400"
										>
											<Trash2 className="w-4 h-4" />
										</button>
									</div>
								))}
							</div>
						</div>
					)}
				</div>

				{/* Fixed Footer */}
				<div className="border-t border-gray-100 dark:border-gray-800">
					{/* Recent Selections */}
					{filteredRecentSelections?.length > 0 && (
						<div className="px-4 pt-3 pb-2">
							<div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
								Recent
							</div>
							<div className="space-y-1">
								{filteredRecentSelections.map((suggestion) => (
									<button
										key={suggestion.email}
										type="button"
										onClick={() => handleSuggestionClick(suggestion)}
										className="w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg flex items-center gap-2"
									>
										<User className="w-4 h-4 text-gray-500 dark:text-gray-400" />
										<div className="flex flex-col">
											<span className="text-sm font-medium text-gray-900 dark:text-gray-100">
												{suggestion.displayName}
											</span>
											<span className="text-xs text-gray-500 dark:text-gray-400">
												{suggestion.email}
											</span>
										</div>
									</button>
								))}
							</div>
						</div>
					)}

					{/* Done Button */}
					{showSendUpdates && <SendUpdatesPopup />}

					<div className="p-4">
						<button
							type="button"
							onClick={handleDone}
							className="w-full px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
						>
							Done
						</button>
					</div>
				</div>
			</div>
		</>
	);
};

export default ParticipantsModal;
